import { useState } from 'react';
import { Loader2, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function yn(v) { return v ? 'yes' : 'no'; }

export function CrossFeedDuplicatesPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.crossFeedDuplicates();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const pairs = (r && r.pairs) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Copy className="h-5 w-5 text-blue-600" />
          Cross-Feed Duplicates (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Candidate same-movement pairs from different sources (e.g. Wise and Xero): same
          entity, account, native amount and currency, dates within +/- 2 days. Read-only,
          no dedup. Use it to decide whether upstream dedup is authoritative.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm" data-testid="cross-feed-run">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
          Run Duplicate Scan
        </Button>

        {r && (
          <div className="space-y-3" data-testid="cross-feed-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>Generated {r.generated_at}</span>
              <span>|</span>
              <span>{r.total_transactions} transactions</span>
              <span>|</span>
              <Badge variant={r.candidate_pair_count > 0 ? 'destructive' : 'outline'}>
                {r.candidate_pair_count} candidate pair(s)
              </Badge>
              {r.skipped_no_amount > 0 && (
                <Badge variant="outline" className="text-amber-600">
                  {r.skipped_no_amount} skipped (no amount)
                </Badge>
              )}
            </div>

            {(r.summary || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {r.summary.map((s, i) => (
                  <Badge key={i} variant="outline">{s.entity_name}: {s.candidate_pairs}</Badge>
                ))}
              </div>
            )}

            {pairs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No cross-feed duplicate candidates found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2">Entity</th>
                      <th className="text-right p-2">Amount</th>
                      <th className="text-left p-2">Sources</th>
                      <th className="text-left p-2">Dates</th>
                      <th className="text-left p-2">Transaction ids</th>
                      <th className="text-center p-2">Reconciled</th>
                      <th className="text-center p-2">On invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.map((p, i) => (
                      <tr key={i} className="border-t" data-testid="cross-feed-row">
                        <td className="p-2">{p.entity_name}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(p.amount, p.currency)}</td>
                        <td className="p-2">{p.sources[0]} &harr; {p.sources[1]}</td>
                        <td className="p-2 font-mono">{p.dates[0] || '—'} / {p.dates[1] || '—'}</td>
                        <td className="p-2 font-mono truncate max-w-[220px]" title={p.transaction_ids.join(', ')}>
                          {p.transaction_ids[0]} / {p.transaction_ids[1]}
                        </td>
                        <td className="p-2 text-center">{yn(p.is_reconciled[0])} / {yn(p.is_reconciled[1])}</td>
                        <td className="p-2 text-center">{yn(p.matched_to_invoice[0])} / {yn(p.matched_to_invoice[1])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
